import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { daylightModeTransitionMs } from "@/lib/daylightModeMotion";
import { useLocation } from "wouter";
import {
  DAYLIGHT_MODE_BAND_LABELS,
  resolveDaylightModeBandFromDate,
  type DaylightModeBand,
} from "@shared/daylightMode";
import { isSalesPortalAmbienceEligiblePath } from "@/lib/daylightModePaths";
import { salesAmbienceSceneImageCss } from "@/lib/salesAmbienceSceneUrls";
import {
  readDaylightModeEnabled,
  readDaylightModePreviewBand,
  writeDaylightModeEnabled,
  writeDaylightModePreviewBand,
} from "@/lib/daylightModeStorage";
import { useAuth } from "@/_core/hooks/useAuth";

type Ctx = {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  band: DaylightModeBand;
  bandLabel: string;
  /** Ambience active on current Sales Portal page */
  activeOnPage: boolean;
  previewBand: DaylightModeBand | null;
  setPreviewBand: (band: DaylightModeBand | null) => void;
};

const DaylightModeContext = createContext<Ctx | null>(null);

const BAND_TICK_MS = 60_000;

function readDevUrlPreviewBand(): DaylightModeBand | null {
  if (!import.meta.env.DEV || typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("daylightPreview");
  if (raw === "dawn" || raw === "morning" || raw === "noon" || raw === "afternoon" || raw === "dusk" || raw === "night") {
    return raw;
  }
  return null;
}

export function DaylightModeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [location] = useLocation();
  const browserPath = typeof window !== "undefined" ? window.location.pathname : location;
  const path = browserPath || location;

  const [enabled, setEnabledState] = useState(() => readDaylightModeEnabled(userId));
  const [liveBand, setLiveBand] = useState<DaylightModeBand>(() => resolveDaylightModeBandFromDate());
  const [previewBand, setPreviewBandState] = useState<DaylightModeBand | null>(() =>
    readDaylightModePreviewBand(userId),
  );

  const salesEligible = isSalesPortalAmbienceEligiblePath(path);
  const urlPreview = readDevUrlPreviewBand();
  const effectivePreview = import.meta.env.DEV ? urlPreview ?? previewBand : null;
  const band = effectivePreview ?? liveBand;
  const activeOnPage = enabled && salesEligible;

  useEffect(() => {
    setEnabledState(readDaylightModeEnabled(userId));
    setPreviewBandState(readDaylightModePreviewBand(userId));
  }, [userId]);

  useEffect(() => {
    const tick = () => setLiveBand(resolveDaylightModeBandFromDate());
    tick();
    const id = window.setInterval(tick, BAND_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const setEnabled = useCallback(
    (on: boolean) => {
      setEnabledState(on);
      writeDaylightModeEnabled(on, userId);
    },
    [userId],
  );

  const setPreviewBand = useCallback(
    (next: DaylightModeBand | null) => {
      setPreviewBandState(next);
      writeDaylightModePreviewBand(next, userId);
    },
    [userId],
  );

  const wasActiveOnPageRef = useRef(false);

  const applySceneImage = useCallback((root: HTMLElement, bandId: DaylightModeBand) => {
    root.style.setProperty("--ambience-scene-image", salesAmbienceSceneImageCss(bandId));
  }, []);

  const clearSceneImage = useCallback((root: HTMLElement) => {
    root.style.removeProperty("--ambience-scene-image");
  }, []);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const wasActive = wasActiveOnPageRef.current;
    wasActiveOnPageRef.current = activeOnPage;

    /* Clear legacy attributes from earlier colour-swap implementation */
    root.removeAttribute("data-daylight-internal");
    root.removeAttribute("data-daylight-mode");
    root.removeAttribute("data-daylight-band");

    if (activeOnPage) {
      root.setAttribute("data-sales-ambience", "on");
      root.setAttribute("data-sales-ambience-band", band);
      applySceneImage(root, band);
      return;
    }

    if (wasActive && salesEligible && root.getAttribute("data-sales-ambience") === "on") {
      const fadeMs = daylightModeTransitionMs();
      root.setAttribute("data-sales-ambience", "off");
      root.setAttribute("data-sales-ambience-band", band);
      applySceneImage(root, band);
      const id = window.setTimeout(() => {
        root.removeAttribute("data-sales-ambience");
        root.removeAttribute("data-sales-ambience-band");
        clearSceneImage(root);
      }, fadeMs);
      return () => window.clearTimeout(id);
    }

    root.removeAttribute("data-sales-ambience");
    root.removeAttribute("data-sales-ambience-band");
    clearSceneImage(root);
  }, [activeOnPage, salesEligible, band, applySceneImage, clearSceneImage]);

  const value = useMemo(
    () => ({
      enabled,
      setEnabled,
      band,
      bandLabel: DAYLIGHT_MODE_BAND_LABELS[band],
      activeOnPage,
      previewBand: effectivePreview,
      setPreviewBand,
    }),
    [enabled, setEnabled, band, activeOnPage, effectivePreview, setPreviewBand],
  );

  return <DaylightModeContext.Provider value={value}>{children}</DaylightModeContext.Provider>;
}

export function useDaylightMode(): Ctx {
  const ctx = useContext(DaylightModeContext);
  if (!ctx) {
    throw new Error("useDaylightMode must be used within DaylightModeProvider");
  }
  return ctx;
}

export function useDaylightModeOptional(): Ctx | null {
  return useContext(DaylightModeContext);
}
